import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mobilizationsApi, projectsApi, usersApi } from '@/lib/apiClient';
import {
  invalidateAssignmentQueries,
  invalidateHealthQueries,
  invalidateReadinessQueries,
} from '@/lib/queryClient';
import { StateView, Badge, Modal, Button } from '@/components';
import Combobox from '@/components/Combobox';
import { fmtDate, toDateInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import { useFieldLeaderGroupMembers } from './FieldLeaderPicker';

// ─── Types (mobilizationsApi / projectsApi return `any`) ────────────────────
interface Mobilization {
  id: number;
  mobSequence: number;
  projectLeader: string | null;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
}

interface ChecklistValue {
  checklistItemId: number;
  valueId: number;
  checklistItem: { id: number; code: string; label: string; isCritical: boolean };
  value: { id: number; label: string };
}

interface ProjectDetail {
  id: number;
  currentStartDate: string | null;
  estimatedCompletion: string | null;
  projectStatus: { id: number; label: string } | null;
  mobilizationStatus: { id: number; label: string } | null;
  checklistValues: ChecklistValue[];
}

// "Schedule Confirmed" is auto-derived server-side and never written by the UI.
// Mirrors apps/api/src/common/checklist.ts SCHEDULE_CONFIRMED_ITEM_ID.
const SCHEDULE_CONFIRMED_ITEM_ID = 14;

// valueId ↔ label — the SAME option set the project edit modal uses
// (ProjectsPage CL_VALUE_OPTS). 1=Yes & 3=N/A are the "done" values.
const CL_VALUE_OPTS = [
  { id: 1, label: 'Yes' },
  { id: 2, label: 'No' },
  { id: 3, label: 'N/A' },
  { id: 4, label: 'Blocked' },
  { id: 5, label: 'Not Set' },
];

// A checklist value counts as "done" when it is Yes (1) or N/A (3) — the exact
// rule the portfolio <ChecklistDots> + readiness KPI use. Kept identical here so
// the tab summary mirrors the rollup dot.
const isDone = (valueId: number) => valueId === 1 || valueId === 3;

// Mirrors apps/api/src/common/checklist.ts CHECKLIST_PRECON_ONLY_CODES /
// CHECKLIST_PRECON_STATUS_LABELS / checklistItemCounts. Items that only apply
// before a job starts are dropped from THIS TALLY for a started job — out of the
// total as well as the done count — so the summary here matches the readiness KPI
// and the portfolio dots. The row is still rendered and still editable; it just
// doesn't score. Keep in step with the API or the three numbers disagree.
const PRECON_ONLY_CODES = new Set(['ConcreteMixDesigns']);
const PRECON_STATUS_LABELS = new Set(['Pipeline', 'Preconstruction']);
const itemCounts = (code: string, statusLabel: string | null | undefined) =>
  !PRECON_ONLY_CODES.has(code) || PRECON_STATUS_LABELS.has(statusLabel ?? 'Pipeline');

// Project statuses that mean the job is mobilized / under construction (vs.
// pipeline / pre-con). Used to collapse the pre-construction checklist by
// default once the job is active. Matches the seeded project_statuses labels.
const ACTIVE_STATUS_LABELS = new Set(['Active', 'Construction']);

// Mirrors RfisTab: alias the shared field/input classes onto the RFI names so
// the modal forms look native, and reuse .pw-card for the list shell.
const css = `
  .mob-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .mob-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .mob-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .mob-table tr:last-child td { border-bottom: 0; }
  .mob-seq { font-variant-numeric: tabular-nums; font-weight: var(--ew-fw-medium); white-space: nowrap; }
  .mob-muted { color: var(--ew-n-500); }
  .mob-actions { display: flex; gap: var(--ew-s-2); justify-content: flex-end; white-space: nowrap; }
  .mob-field { display: flex; flex-direction: column; }
  .mob-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .mob-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
  .mob-input:disabled { background: var(--ew-n-50); color: var(--ew-n-500); cursor: not-allowed; }
  .mob-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ew-s-3); }
  .mob-summary { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  /* Checklist editor grid — 2 cols, 3 on wide viewports. */
  .mob-cl-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--ew-s-3) var(--ew-s-4); }
  @media (min-width: 900px) { .mob-cl-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  .mob-cl-hd { display: flex; align-items: center; justify-content: space-between; gap: var(--ew-s-3); flex-wrap: wrap; }
  .mob-cl-toggle { background: none; border: none; padding: 0; font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew); cursor: pointer; }
  .mob-readiness { display: inline-flex; align-items: center; gap: var(--ew-s-2); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); border-radius: 999px; padding: 3px 12px; }
`;

const EMPTY_FORM = {
  projectLeader: '',
  startDate: '',
  endDate: '',
  notes: '',
};
type FormState = typeof EMPTY_FORM;

export default function MobilizationTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['mobilizations', projectId],
    queryFn: () => mobilizationsApi.list(projectId) as Promise<Mobilization[]>,
  });

  // The project detail feeds the checklist editor, the readiness summary, and
  // the active/mobilized collapse heuristic. Cached under the same key the rest
  // of the workspace uses so a checklist save (which invalidates it) refreshes
  // both this tab and the portfolio dots.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<ProjectDetail>,
  });

  // Permission gating — mirrors the backend guards so non-permitted users never
  // see actions that would 403. Mobilization POST/PATCH require
  // 'mobilizations.edit'; DELETE requires 'admin.access'. The checklist update
  // endpoint requires 'projects.edit'. Admin role short-circuits all of them.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const isAdmin = me?.roles.includes('Admin') ?? false;
  // SYSTAC bids are edit-locked (read-only) — no crew scheduling or checklist
  // edits until Closed-Won. Overrides the permission grant; API enforces it too.
  const canEdit = (isAdmin || (me?.permissions.includes('mobilizations.edit') ?? false)) && !locked;
  const canDelete = isAdmin || (me?.permissions.includes('admin.access') ?? false);
  const canEditChecklist = (isAdmin || (me?.permissions.includes('projects.edit') ?? false)) && !locked;

  // Modal state: null = closed; { id: null } = Add; { id: n } = Edit.
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // Pending delete confirmation (the row being removed).
  const [confirmDelete, setConfirmDelete] = useState<Mobilization | null>(null);

  const set = (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const openAdd = () => {
    // First mobilization = the job dates: when no phases exist yet, pre-fill the
    // form from the project's current start / est. completion so the initial
    // mobilization is one confirm away. Subsequent adds start blank.
    const noPhasesYet = (query.data?.length ?? 0) === 0;
    const p = projectQuery.data;
    setForm(
      noPhasesYet && p
        ? {
            projectLeader: '',
            startDate: toDateInputValue(p.currentStartDate),
            endDate: toDateInputValue(p.estimatedCompletion),
            notes: '',
          }
        : EMPTY_FORM,
    );
    setEditing({ id: null });
  };
  const openEdit = (m: Mobilization) => {
    setForm({
      projectLeader: m.projectLeader ?? '',
      startDate: toDateInputValue(m.startDate),
      endDate: toDateInputValue(m.endDate),
      notes: m.notes ?? '',
    });
    setEditing({ id: m.id });
  };
  const closeModal = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  // Field Leaders group members feed the "Project leader" type-ahead. Loaded
  // lazily while the modal is open; if Graph is unavailable (consent, offline)
  // the field degrades to plain free text — same as before the picker.
  const membersQuery = useFieldLeaderGroupMembers(editing != null && canEdit);
  const members = membersQuery.data ?? [];
  const leaderOptions = useMemo(
    () => members.map((m) => ({ value: m.displayName, sub: m.email ?? undefined })),
    [members],
  );
  // The typed/picked name links to a group member only on an exact,
  // unambiguous display-name match — free text (legacy names, subs from
  // outside the group) stays text-only, exactly like the old field.
  const matchedLeader = useMemo(() => {
    const name = form.projectLeader.trim().toLowerCase();
    if (!name) return null;
    const hits = members.filter((m) => m.displayName.trim().toLowerCase() === name);
    return hits.length === 1 ? hits[0] : null;
  }, [members, form.projectLeader]);

  // Send empty strings as null (clear) — matches the API's patch semantics.
  const payload = () => {
    const ne = (v: string) => (v.trim() === '' ? null : v.trim());
    return {
      projectLeader: ne(form.projectLeader),
      // Linked member (or explicit clear). The API provisions the users row,
      // stores the reference, and auto-assigns them as a project field leader.
      projectLeaderEntraId: matchedLeader?.id ?? null,
      projectLeaderEmail: matchedLeader?.email ?? undefined,
      startDate: ne(form.startDate),
      endDate: ne(form.endDate),
      notes: ne(form.notes),
    };
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = payload();
      // mobSequence is auto-assigned server-side when omitted (create).
      return editing?.id != null
        ? mobilizationsApi.update(projectId, editing.id, body)
        : mobilizationsApi.create(projectId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mobilizations', projectId] });
      // The tab badge / overview reads project-counts (mobilizations w/o end_date).
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
      // The Overview's Target Completion is effectiveCompletion = later of the
      // stored completion and the newest mob end (server-derived, live). Adding
      // or editing a mob changes it, so refresh the project detail or the KPI
      // shows stale until a hard reload.
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      // A picked leader is auto-assigned to the project's field leaders —
      // refresh the Team section so the new chip shows without a reload.
      qc.invalidateQueries({ queryKey: ['field-leaders', projectId] });
      // ⚠️ That auto-assignment is an ELIGIBILITY change, not just a chip.
      // `resolveAndAssignLeader` inserts into `project_field_leaders`
      // (mobilizations.service.ts), which `activeAssignmentWhere` tests for —
      // so picking YOURSELF here on your first live job turns the Today tab
      // on. (Codex, #510.)
      void invalidateAssignmentQueries(qc, projectId);
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => mobilizationsApi.delete(projectId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mobilizations', projectId] });
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
      // Removing a mob drops it from the effectiveCompletion max, so the
      // Overview's Target Completion must refetch (else it keeps the old date).
      // ⚠️ AND SO MUST BOTH TODAY PAGES — the same drop can turn a job excused
      // by a sanctioned remob back into a late one, and `deriveHealth` computes
      // that on read, so nothing else announces it. (Codex, P2 on #513.)
      void invalidateHealthQueries(qc, projectId);
      setConfirmDelete(null);
    },
  });

  const isEdit = editing?.id != null;

  return (
    <>
      <style>{css}</style>

      {/* Mobilization checklist — relocated from the project edit modal. The
          editable copy lives here; ProjectsPage keeps read-only glance views. */}
      <ChecklistCard
        projectId={projectId}
        query={projectQuery}
        canEdit={canEditChecklist}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <SummaryLine query={query} />
        {canEdit ? <button className="btn btn-primary" onClick={openAdd}>+ Add phase</button> : null}
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">Mobilization phases</h2>
        <StateView query={query} empty="No mobilization phases logged yet.">
          {(rows) => {
            // Defensive client-side sort by sequence (API already orders ASC).
            const ordered = [...rows].sort((a, b) => a.mobSequence - b.mobSequence);
            return (
              <div className="ew-table-scroll">
              <table className="mob-table ew-cards">
                <thead>
                  <tr>
                    <th>Seq</th>
                    <th>Project leader</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Notes</th>
                    {(canEdit || canDelete) ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((m) => (
                    <tr key={m.id}>
                      <td className="mob-seq" data-label="Seq">{m.mobSequence}</td>
                      <td data-label="Project leader">{m.projectLeader ?? <span className="mob-muted">—</span>}</td>
                      <td className="mob-muted" data-label="Start" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.startDate)}</td>
                      <td data-label="End" style={{ whiteSpace: 'nowrap' }}>
                        {m.endDate
                          ? fmtDate(m.endDate)
                          : <Badge label="In progress" tone="info" />}
                      </td>
                      <td className="mob-muted" data-label="Notes">{m.notes ?? '—'}</td>
                      {(canEdit || canDelete) ? (
                        <td>
                          <div className="mob-actions">
                            {canEdit ? <Button variant="secondary" size="sm" onClick={() => openEdit(m)}>Edit</Button> : null}
                            {canDelete ? <Button variant="danger" size="sm" onClick={() => setConfirmDelete(m)}>Remove</Button> : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            );
          }}
        </StateView>
      </div>

      {/* Add / Edit modal */}
      <Modal
        isOpen={editing != null}
        onClose={closeModal}
        title={isEdit ? 'Edit mobilization phase' : 'Add mobilization phase'}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saveMutation.isPending}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
              {isEdit ? 'Save changes' : 'Add phase'}
            </Button>
          </>
        }
      >
        {saveMutation.isError && (
          <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
            {(saveMutation.error as Error).message}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
          <div className="mob-field">
            <label className="mob-label">Project leader</label>
            <Combobox
              value={form.projectLeader}
              onChange={(v) => setForm((p) => ({ ...p, projectLeader: v }))}
              options={leaderOptions}
              placeholder="Pick from the Field Leaders group…"
            />
            <span style={{ font: 'var(--ew-fw-regular) 11px/1.4 var(--ew-font)', marginTop: 4, color: matchedLeader ? 'var(--ew-green-700)' : 'var(--ew-n-500)' }}>
              {matchedLeader
                ? `✓ ${matchedLeader.displayName} will also be assigned as a field leader on this job (grants Project Tracker access).`
                : 'Picking from the list also assigns them as a field leader on this job. Free-typed names are display-only.'}
            </span>
          </div>
          <div className="mob-grid2">
            <div className="mob-field">
              <label className="mob-label">Start date</label>
              <input className="mob-input" type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={form.startDate} onChange={set('startDate')} />
            </div>
            <div className="mob-field">
              <label className="mob-label">End date</label>
              <input className="mob-input" type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={form.endDate} onChange={set('endDate')} />
            </div>
          </div>
          <div className="mob-field">
            <label className="mob-label">Notes</label>
            <textarea className="mob-input" style={{ minHeight: 90, resize: 'vertical' }} value={form.notes} onChange={set('notes')} />
          </div>
        </div>
      </Modal>

      {/* Remove confirmation */}
      <Modal
        isOpen={confirmDelete != null}
        onClose={() => setConfirmDelete(null)}
        title="Remove mobilization phase?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending}>Cancel</Button>
            <Button variant="danger" onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)} isLoading={deleteMutation.isPending}>
              Remove
            </Button>
          </>
        }
      >
        {deleteMutation.isError && (
          <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
            {(deleteMutation.error as Error).message}
          </div>
        )}
        <p style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body) var(--ew-font)', color: 'var(--ew-dark)', margin: 0 }}>
          {confirmDelete
            ? <>Remove phase <b>#{confirmDelete.mobSequence}</b>{confirmDelete.projectLeader ? <> ({confirmDelete.projectLeader})</> : null}? This can't be undone.</>
            : null}
        </p>
      </Modal>
    </>
  );
}

// ─── Checklist card (relocated from the edit modal) ─────────────────────────

function ChecklistCard({
  projectId,
  query,
  canEdit,
}: {
  projectId: number;
  query: { data?: ProjectDetail; isLoading: boolean; isError: boolean };
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const project = query.data;
  const items = project?.checklistValues ?? [];

  // Pending edits keyed by checklistItemId → valueId; batched and flushed on Save
  // (mirrors the edit modal's clEdits). The Save button is disabled until dirty.
  const [edits, setEdits] = useState<Record<number, number>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Readiness: done = items whose effective value is Yes (1) or N/A (3) — the
  // exact rule the dots use. Pending edits are reflected live so the summary
  // tracks the selects before save.
  const effective = (cv: ChecklistValue) => edits[cv.checklistItemId] ?? cv.valueId;
  const statusLabel = project?.projectStatus?.label ?? null;
  // Only items that apply at this job's stage are scored (see itemCounts above).
  const scored = items.filter((cv) => itemCounts(cv.checklistItem.code, statusLabel));
  const done = scored.filter((cv) => isDone(effective(cv))).length;
  const total = scored.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  // Collapse the (pre-construction) checklist by default once the job is
  // mobilized/active; expanded by default otherwise. Always toggleable.
  const isActive = useMemo(() => {
    const label = project?.projectStatus?.label ?? '';
    return ACTIVE_STATUS_LABELS.has(label);
  }, [project?.projectStatus?.label]);
  const [open, setOpen] = useState<boolean | null>(null);
  // null = follow the default (derived from status); once the user toggles, honor that.
  const expanded = open ?? !isActive;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = Object.entries(edits).map(([k, v]) => ({ checklistItemId: Number(k), valueId: v }));
      return projectsApi.updateChecklist(projectId, payload);
    },
    onSuccess: () => {
      setEdits({});
      setSaveError(null);
      // Refresh this tab's project (checklist + readiness) AND the portfolio
      // dots / readiness KPI, which read off the same checklist data.
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      // ⚠️ Clears ['today'] as well as the project caches. Both Today pages
      // score this same checklist — the PM strip and the leadership readiness
      // section — and neither was refreshed here, so qualifying a job's last
      // critical criterion left the row on screen until the five-minute poll.
      void invalidateReadinessQueries(qc, projectId);
    },
    onError: (e: any) => setSaveError(e?.message ?? 'Failed to save checklist'),
  });

  const dirty = Object.keys(edits).length > 0;

  // Readiness pill tint: green at 100%, amber when partial, neutral when none.
  const readiness =
    total === 0
      ? { bg: 'var(--ew-n-100)', color: 'var(--ew-n-500)' }
      : done === total
        ? { bg: '#E6F2EB', color: '#015A28' }
        : { bg: '#FDECD9', color: '#9E4E10' };

  if (query.isLoading) {
    return (
      <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd">Mobilization checklist</h2>
        <p className="mob-summary" style={{ margin: 0 }}>Loading…</p>
      </div>
    );
  }
  if (query.isError || !project) {
    return (
      <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd">Mobilization checklist</h2>
        <p className="mob-summary" style={{ margin: 0 }}>Couldn't load the checklist.</p>
      </div>
    );
  }
  if (total === 0) {
    return (
      <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd">Mobilization checklist</h2>
        <p className="mob-summary" style={{ margin: 0 }}>No checklist items for this project.</p>
      </div>
    );
  }

  return (
    <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
      <div className="mob-cl-hd" style={{ marginBottom: expanded ? 'var(--ew-s-4)' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
          <h2 className="pw-card-hd" style={{ margin: 0, border: 0, padding: 0 }}>Mobilization checklist</h2>
          <span className="mob-readiness" style={{ background: readiness.bg, color: readiness.color }}>
            {done} of {total} complete · {pct}%
          </span>
        </div>
        <button
          type="button"
          className="mob-cl-toggle"
          aria-expanded={expanded}
          onClick={() => setOpen(!expanded)}
        >
          {expanded ? 'Collapse ▲' : 'Expand ▼'}
        </button>
      </div>

      {expanded && (
        <>
          {saveError && (
            <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
              {saveError}
            </div>
          )}
          <div className="mob-cl-grid">
            {items.map((cv) => {
              const readOnly = cv.checklistItemId === SCHEDULE_CONFIRMED_ITEM_ID || !canEdit;
              return (
                <div key={cv.checklistItemId} className="mob-field">
                  <label className="mob-label">{cv.checklistItem.label}</label>
                  <select
                    className="mob-input"
                    disabled={readOnly}
                    value={effective(cv)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setEdits((p) => ({ ...p, [cv.checklistItemId]: v }));
                    }}
                  >
                    {CL_VALUE_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  {cv.checklistItemId === SCHEDULE_CONFIRMED_ITEM_ID && (
                    <span style={{ font: 'var(--ew-fw-regular) 11px/1.3 var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 4 }}>
                      Auto-derived from the schedule
                    </span>
                  )}
                  {/* Shown but not scored — say so, or an item that can never move
                      the percentage looks like the percentage is broken. */}
                  {!itemCounts(cv.checklistItem.code, statusLabel) && (
                    <span style={{ font: 'var(--ew-fw-regular) 11px/1.3 var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 4 }}>
                      Not counted once the job has started
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--ew-s-4)' }}>
              <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending} disabled={!dirty}>
                Save checklist
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// A light "finished of total complete" nod to progress — finished = phases with
// an end date. Renders nothing until the list resolves.
function SummaryLine({ query }: { query: { data?: Mobilization[] } }) {
  const rows = query.data;
  if (!rows || rows.length === 0) return <span />;
  const finished = rows.filter((m) => !!m.endDate).length;
  return <span className="mob-summary">{finished} of {rows.length} complete</span>;
}
